import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * CC0 models (public/models, prepared by scripts/process_models.mjs) turned into what the city
 * instances (DECISIONS "Models"):
 * - cars: one merged, flat-shaded geometry each, facing +x, wheels on y = 0, scaled to a real length.
 *   Vertex colours come from the materials; `carPaint` = 1 on the body panels, which the car shader
 *   tints per instance (glass, tyres and lights keep their colours);
 * - trees: one geometry per variant with two groups (bark, leaves) and their textured materials,
 *   trunk at the origin, about 7 m tall;
 * - people: the Walk and Idle clips baked into a vertex animation texture (positions and normals per
 *   frame), played back in the vertex shader, so every walker is one instance of one draw call per
 *   model. `tint` marks the vertices recoloured per person (1 shirt, 2 trousers, 3 hair).
 */

export interface CarModel {
  name: string;
  geometry: THREE.BufferGeometry;
}

export interface TreeModel {
  geometry: THREE.BufferGeometry;
  materials: THREE.Material[];
}

export interface PersonModel {
  name: string;
  geometry: THREE.BufferGeometry;
  /** Positions (xyz) and normals (xyz, in the second half of the rows) per vertex per frame. */
  vat: THREE.DataTexture;
  vertices: number;
  /** Texture width and rows per frame (vertex i of frame f at x = i % width, y = f * rows + ⌊i / width⌋). */
  width: number;
  rows: number;
  /** Frames and seconds per clip: walk first, then idle. */
  walk: { start: number; frames: number; seconds: number };
  idle: { start: number; frames: number; seconds: number };
}

const CAR_LENGTH: Record<string, number> = { 'car-sedan': 4.5, 'car-sports': 4.4, 'car-suv': 4.7, 'car-coupe': 4.3 };

export class ModelLibrary {
  cars: CarModel[] = [];
  trees: TreeModel[] = [];
  people: PersonModel[] = [];
  loaded = false;
  private readonly listeners: (() => void)[] = [];

  onLoad(fn: () => void): void {
    if (this.loaded) fn();
    else this.listeners.push(fn);
  }

  async load(base = import.meta.env.BASE_URL + 'models/'): Promise<void> {
    const loader = new GLTFLoader();
    const get = (n: string) => loader.loadAsync(`${base}${n}.glb`);
    const manifest = (await (await fetch(`${base}manifest.json`)).json()) as Record<string, { kind: string; paint: string[] }>;
    const entries = Object.entries(manifest);
    const gltfs = await Promise.all(entries.map(([n]) => get(n)));
    entries.forEach(([name, info], i) => {
      const g = gltfs[i];
      if (info.kind === 'car') this.cars.push({ name, geometry: carGeometry(g.scene, info.paint, CAR_LENGTH[name] ?? 4.5) });
      else if (info.kind === 'tree') this.trees.push(...treeModels(g.scene));
      else if (info.kind === 'person') this.people.push(bakePerson(name, g));
    });
    this.loaded = true;
    for (const fn of this.listeners) fn();
  }
}

/** Bake the scene's world transforms into its meshes' geometries (children of the root). */
function bakedMeshes(root: THREE.Object3D): THREE.Mesh[] {
  root.updateMatrixWorld(true);
  const out: THREE.Mesh[] = [];
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && !(o as THREE.SkinnedMesh).isSkinnedMesh) out.push(o as THREE.Mesh);
  });
  return out;
}

function carGeometry(scene: THREE.Object3D, paint: string[], length: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (const mesh of bakedMeshes(scene)) {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const src = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
    src.applyMatrix4(mesh.matrixWorld);
    const groups = src.groups.length ? src.groups : [{ start: 0, count: src.getAttribute('position').count, materialIndex: 0 }];
    for (const gr of groups) {
      const mat = mats[gr.materialIndex ?? 0] as THREE.MeshStandardMaterial;
      const pos = src.getAttribute('position');
      const n = gr.count;
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute((pos.array as Float32Array).slice(gr.start * 3, (gr.start + n) * 3), 3));
      const isPaint = paint.includes(mat.name);
      const c = isPaint ? new THREE.Color(1, 1, 1) : mat.color.clone();
      if (mat.emissive && mat.emissive.getHex() !== 0) c.add(mat.emissive);
      const col = new Float32Array(n * 3);
      for (let k = 0; k < n; k++) c.toArray(col, k * 3);
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      g.setAttribute('carPaint', new THREE.BufferAttribute(new Float32Array(n).fill(isPaint ? 1 : 0), 1));
      parts.push(g);
    }
  }
  const g = mergeGeometries(parts)!;
  // Forward +z → +x, centred, wheels on the ground, real length; flat normals.
  g.rotateY(Math.PI / 2);
  g.computeBoundingBox();
  const b = g.boundingBox!;
  g.translate(-(b.min.x + b.max.x) / 2, -b.min.y, -(b.min.z + b.max.z) / 2);
  const s = length / (b.max.x - b.min.x);
  g.scale(s, s, s);
  g.computeVertexNormals();
  g.computeBoundingBox();
  g.computeBoundingSphere();
  return g;
}

function treeModels(scene: THREE.Object3D): TreeModel[] {
  // A multi-material glTF mesh loads as a group of one mesh per primitive (bark, leaves): one tree per
  // parent, merged into one geometry with a group per material.
  const byParent = new Map<THREE.Object3D, THREE.Mesh[]>();
  for (const mesh of bakedMeshes(scene)) {
    const key = mesh.parent && mesh.parent !== scene && mesh.parent.children.every((c) => (c as THREE.Mesh).isMesh) ? mesh.parent : mesh;
    const l = byParent.get(key);
    if (l) l.push(mesh);
    else byParent.set(key, [mesh]);
  }
  const out: TreeModel[] = [];
  for (const meshes of byParent.values()) {
    const geos: THREE.BufferGeometry[] = [];
    const mats: THREE.Material[] = [];
    for (const mesh of meshes) {
      const g = mesh.geometry.clone();
      g.applyMatrix4(mesh.matrixWorld);
      for (const k of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(k)) g.deleteAttribute(k);
      geos.push(g);
      const c = (mesh.material as THREE.MeshStandardMaterial).clone();
      if (/leaves|leaf/i.test(c.name)) {
        c.alphaTest = 0.5;
        c.transparent = false;
        c.side = THREE.DoubleSide;
      }
      c.roughness = 0.9;
      c.metalness = 0;
      mats.push(c);
    }
    const g = mergeGeometries(geos, true);
    if (!g) continue;
    g.computeBoundingBox();
    const b = g.boundingBox!;
    const s = 7 / Math.max(0.01, b.max.y - b.min.y);
    g.translate(-(b.min.x + b.max.x) / 2, -b.min.y, -(b.min.z + b.max.z) / 2);
    g.scale(s, s, s);
    g.computeBoundingBox();
    g.computeBoundingSphere();
    out.push({ geometry: g, materials: mats });
  }
  return out;
}

const TINT: [RegExp, number][] = [
  [/shirt|jacket|dress|top/i, 1],
  [/pants|trousers|skirt/i, 2],
  [/hair|eyebrow/i, 3],
  [/skin/i, 4],
];

/** Bake Walk and Idle into a vertex animation texture (see PersonModel). */
function bakePerson(name: string, gltf: { scene: THREE.Object3D; animations: THREE.AnimationClip[] }): PersonModel {
  const scene = gltf.scene;
  scene.updateMatrixWorld(true);
  const skinned: THREE.SkinnedMesh[] = [];
  scene.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned.push(o as THREE.SkinnedMesh);
  });
  // Rest geometry: all skinned primitives merged, non-indexed, with colour and tint per vertex.
  const parts: { mesh: THREE.SkinnedMesh; start: number; count: number; color: THREE.Color; tint: number }[] = [];
  let total = 0;
  for (const sm of skinned) {
    const mats = Array.isArray(sm.material) ? sm.material : [sm.material];
    const idx = sm.geometry.index;
    const groups = sm.geometry.groups.length ? sm.geometry.groups : [{ start: 0, count: idx ? idx.count : sm.geometry.getAttribute('position').count, materialIndex: 0 }];
    for (const gr of groups) {
      const mat = mats[gr.materialIndex ?? 0] as THREE.MeshStandardMaterial;
      const tint = TINT.find(([re]) => re.test(mat.name))?.[1] ?? 0;
      parts.push({ mesh: sm, start: gr.start, count: gr.count, color: mat.color.clone(), tint });
      total += gr.count;
    }
  }
  const walk = gltf.animations.find((a) => /Walk$/.test(a.name))!;
  const idle = gltf.animations.find((a) => /Idle$/.test(a.name)) ?? walk;
  const WALK_FRAMES = 24;
  const IDLE_FRAMES = 16;
  const frames = WALK_FRAMES + IDLE_FRAMES;
  const width = 1024;
  const rows = Math.ceil(total / width);
  // Two halves: positions, then normals.
  const data = new Float32Array(width * rows * frames * 2 * 4);
  const mixer = new THREE.AnimationMixer(scene);
  const v = new THREE.Vector3();
  // Model space → ours: +z forward → +x, feet on y = 0, 1.75 m tall (from the rest pose).
  const fix = new THREE.Matrix4().makeRotationY(Math.PI / 2);
  const framePositions = (): Float32Array => {
    scene.updateMatrixWorld(true);
    for (const sm of skinned) sm.skeleton.update();
    const out = new Float32Array(total * 3);
    let k = 0;
    for (const p of parts) {
      const idx = p.mesh.geometry.index;
      for (let j = 0; j < p.count; j++) {
        const vi = idx ? idx.getX(p.start + j) : p.start + j;
        p.mesh.getVertexPosition(vi, v);
        v.applyMatrix4(p.mesh.matrixWorld).applyMatrix4(fix);
        v.toArray(out, k * 3);
        k++;
      }
    }
    return out;
  };
  const restPose = framePositions();
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 1; i < restPose.length; i += 3) {
    minY = Math.min(minY, restPose[i]);
    maxY = Math.max(maxY, restPose[i]);
  }
  const s = 1.75 / Math.max(0.01, maxY - minY);
  const write = (frame: number, pos: Float32Array) => {
    // Flat normals per triangle (non-indexed: vertices come in threes).
    const nrm = new Float32Array(total * 3);
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    for (let t = 0; t + 2 < total; t += 3) {
      a.fromArray(pos, t * 3);
      b.fromArray(pos, t * 3 + 3);
      c.fromArray(pos, t * 3 + 6);
      const n = b.sub(a).cross(c.sub(a)).normalize();
      for (let q = 0; q < 3; q++) n.toArray(nrm, (t + q) * 3);
    }
    for (let i = 0; i < total; i++) {
      const x = i % width;
      const y = Math.floor(i / width);
      const o = ((frame * rows + y) * width + x) * 4;
      data[o] = pos[i * 3] * s;
      data[o + 1] = (pos[i * 3 + 1] - minY) * s;
      data[o + 2] = pos[i * 3 + 2] * s;
      const on = o + width * rows * frames * 4;
      data[on] = nrm[i * 3];
      data[on + 1] = nrm[i * 3 + 1];
      data[on + 2] = nrm[i * 3 + 2];
    }
  };
  const bake = (clip: THREE.AnimationClip, start: number, n: number) => {
    mixer.stopAllAction();
    const action = mixer.clipAction(clip);
    action.play();
    for (let f = 0; f < n; f++) {
      mixer.setTime((f / n) * clip.duration);
      write(start + f, framePositions());
    }
  };
  bake(walk, 0, WALK_FRAMES);
  bake(idle, WALK_FRAMES, IDLE_FRAMES);
  // Half floats: about 1 mm at human scale, half the memory.
  const half = new Uint16Array(data.length);
  for (let i = 0; i < data.length; i++) half[i] = THREE.DataUtils.toHalfFloat(data[i]);
  const vat = new THREE.DataTexture(half, width, rows * frames * 2, THREE.RGBAFormat, THREE.HalfFloatType);
  vat.minFilter = vat.magFilter = THREE.NearestFilter;
  vat.needsUpdate = true;
  // The instanced geometry: rest positions (only for bounds), colours and tints; the shader reads the VAT.
  const geo = new THREE.BufferGeometry();
  const rest = new Float32Array(total * 3);
  for (let i = 0; i < total; i++) {
    rest[i * 3] = restPose[i * 3] * s;
    rest[i * 3 + 1] = (restPose[i * 3 + 1] - minY) * s;
    rest[i * 3 + 2] = restPose[i * 3 + 2] * s;
  }
  geo.setAttribute('position', new THREE.BufferAttribute(rest, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(total * 3).fill(0), 3));
  const col = new Float32Array(total * 3);
  const tint = new Float32Array(total);
  let k = 0;
  for (const p of parts)
    for (let j = 0; j < p.count; j++) {
      p.color.toArray(col, k * 3);
      tint[k++] = p.tint;
    }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.setAttribute('tint', new THREE.BufferAttribute(tint, 1));
  geo.computeBoundingSphere();
  geo.boundingSphere!.radius += 0.5;
  return {
    name,
    geometry: geo,
    vat,
    vertices: total,
    width,
    rows,
    walk: { start: 0, frames: WALK_FRAMES, seconds: walk.duration },
    idle: { start: WALK_FRAMES, frames: IDLE_FRAMES, seconds: idle.duration },
  };
}
